/**
 * Generic debounced autosave. Calls `save(value)` once after the user has
 * stopped changing `value` for `delayMs`. Cleans up on unmount and on
 * value change (cancels in-flight timer).
 *
 * `enabled=false` short-circuits — used by CommentsPage so the autosave
 * doesn't fire on the initial hydrate-from-server render.
 *
 * Returns a `flush()` callback the consumer can call on close/submit to push
 * the most recent value before unmount (avoids losing the last 0-delayMs of
 * typing). It returns the save's promise so callers can `await flush()` and
 * be sure the save landed before firing a dependent request — CommentsPage
 * awaits it before submit so the autosave PUT can't race the submit POST
 * (a late PUT would re-create a draft row the submit just consumed).
 */
import { useCallback, useEffect, useRef } from 'react';

interface UseAutosaveOptions<T> {
  value: T;
  save: (v: T) => Promise<unknown> | unknown;
  delayMs?: number;
  enabled?: boolean;
}

export function useAutosave<T>({
  value,
  save,
  delayMs = 2000,
  enabled = true,
}: UseAutosaveOptions<T>): { flush: () => Promise<unknown> } {
  const saveRef = useRef(save);
  const valueRef = useRef(value);
  const timerRef = useRef<number | null>(null);

  saveRef.current = save;
  valueRef.current = value;

  useEffect(() => {
    if (!enabled) return undefined;
    if (timerRef.current != null) {
      window.clearTimeout(timerRef.current);
    }
    timerRef.current = window.setTimeout(() => {
      void saveRef.current(valueRef.current);
      timerRef.current = null;
    }, delayMs);
    return () => {
      if (timerRef.current != null) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [value, delayMs, enabled]);

  const flush = useCallback(async () => {
    if (timerRef.current != null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    return saveRef.current(valueRef.current);
  }, []);

  return { flush };
}
